import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { MapPin, Clock, Star, Truck, Utensils, Phone, Mail, ArrowLeft, ShoppingCart } from 'lucide-react'
import { Store, Product } from '../types'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import StoreImage from '../components/StoreImage'
import ProductImage from '../components/ProductImage'
import { calculateStoreIsOpen } from '../lib/storeUtils'
import SEO from '../components/SEO'
import { getCache, getCacheStale, setCache } from '../lib/cache'
import { showOfflineToast } from '../lib/toastManager'
import { useStoreDetail } from '../contexts/StoreDetailContext'

const StoreProfile = () => {
  const { storeId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const { getStore, getStoreSync } = useStoreDetail() // Usar StoreDetailContext en lugar de cargar directamente

  const [store, setStore] = useState<Store | null>(null)
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  // Refs para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingStoreRef = useRef(false)
  const loadingProductsRef = useRef(false)

  useEffect(() => {
    if (!storeId) return
    
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingStoreRef.current || loadingProductsRef.current) return
    
    loadingStoreRef.current = true
    loadingProductsRef.current = true

    Promise.all([
      loadStore().finally(() => { loadingStoreRef.current = false }),
      loadFeaturedProducts().finally(() => { loadingProductsRef.current = false })
    ])
  }, [storeId])

  const loadStore = async () => {
    if (!storeId) return

    // Intentar cargar desde cache sincrónicamente primero (más rápido)
    const cachedStore = getStoreSync(storeId)
    if (cachedStore) {
      setStore(cachedStore)
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }

    try {
      setLoading(true)
      // Usar StoreDetailContext para cargar el store (elimina query duplicada)
      // StoreDetailContext ahora tiene protección contra llamadas simultáneas
      const loadedStore = await getStore(storeId)
      if (loadedStore) {
        setStore(loadedStore)
      } else {
        toast.error('Error al cargar información del negocio')
      }
    } catch (error: any) {
      console.error('Error loading store:', error)
      toast.error('Error al cargar información del negocio')
    } finally {
      setLoading(false)
    }
  }

  const loadFeaturedProducts = async () => {
    if (!storeId) return

    const cacheKey = `store_featured_products_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedProducts = getCacheStale<Product[]>(cacheKey)
    if (cachedProducts && cachedProducts.length > 0) {
      setFeaturedProducts(cachedProducts)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }

    try {
      const response = await api.get(`/products?storeId=${storeId}&isFeatured=true&limit=6`)
      if (response.data.success) {
        // Normalizar los productos para asegurar que price sea un número y mapear image_url
        const normalizedProducts = (response.data.data || []).map((product: any) => ({
          ...product,
          price: Number(product.price) || 0,
          imageUrl: product.image_url || product.imageUrl, // Mapear image_url a imageUrl
          stockQuantity: Number(product.stock_quantity) || 0,
          isAvailable: product.is_available !== false,
          isFeatured: product.is_featured || false
        }))
        setFeaturedProducts(normalizedProducts)
        
        // Guardar en cache (30 minutos de TTL)
        setCache(cacheKey, normalizedProducts, 30 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading featured products:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedProducts) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    }
  }

  const handleViewCatalog = () => {
    if (store) {
      navigate(`/stores/${store.id}/catalog`)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-3 sm:px-4 safe-area-top safe-area-bottom">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Cargando información...</p>
        </div>
      </div>
    )
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-3 sm:px-4 safe-area-top safe-area-bottom">
        <div className="text-center max-w-md">
          <ShoppingCart className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-3 sm:mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Negocio no encontrado</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">El negocio que buscas no existe o no está disponible</p>
          <Link
            to="/stores"
            className="inline-block px-5 sm:px-6 py-2.5 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm sm:text-base min-h-[44px] flex items-center justify-center"
          >
            Ver Negocios
          </Link>
        </div>
      </div>
    )
  }

  // Determinar si el negocio está abierto
  // Considera isActive (que ya incluye is_active e is_open), status y horarios
  const isOpen = store.isActive && store.status === 'approved'

  return (
    <>
      <SEO
        title={store ? `${store.name} - ColmaFácil` : 'Negocio - ColmaFácil'}
        description={store ? `${store.description || `Compra en ${store.name}. ${store.offersDelivery ? 'Entrega disponible' : ''} Productos frescos y de calidad.`}` : 'Negocio en ColmaFácil'}
        keywords={store ? `${store.name}, colmado ${store.address}, delivery ${store.name}, productos frescos, comprar online RD` : 'colmado, negocio, delivery'}
        image={store?.coverImageUrl ? `https://colmafacil.store${store.coverImageUrl}` : undefined}
        url={`https://colmafacil.store/stores/${storeId}`}
        type="store"
        structuredData={store ? {
          "@context": "https://schema.org",
          "@type": "Store",
          "name": store.name,
          "description": store.description,
          "image": store.coverImageUrl ? `https://colmafacil.store${store.coverImageUrl}` : undefined,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": store.address,
            "addressCountry": "DO"
          },
          "telephone": store.phone,
          "email": store.email,
          "url": `https://colmafacil.store/stores/${store.id}`,
          "priceRange": "$$",
          "aggregateRating": store.rating ? {
            "@type": "AggregateRating",
            "ratingValue": store.rating,
            "reviewCount": store.reviewCount || 0
          } : undefined
        } : undefined}
      />
      <div className="min-h-screen bg-gray-50 pt-12 sm:pt-16 safe-area-top">
      {/* Cover Image - iPhone Optimized */}
      <div className="h-40 sm:h-48 md:h-64 bg-gradient-to-br from-primary-500 to-primary-700 relative overflow-hidden">
        <StoreImage
          imageKey={store.coverImageUrl || null}
          type="cover"
          className="w-full h-full object-cover"
          alt={store.name}
          storeId={store.id}
        />
        <div className="absolute inset-0 bg-black/30"></div>
        {/* Back Button Overlay - iPhone Optimized */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 sm:top-4 sm:left-4 p-2 sm:p-2.5 bg-white/90 backdrop-blur-sm rounded-lg sm:rounded-xl hover:bg-white transition-colors shadow-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900" />
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 -mt-12 sm:-mt-16 relative z-10">
        {/* Store Header Card - iPhone Optimized */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col md:flex-row gap-4 sm:gap-5 md:gap-6">
            {/* Logo - iPhone Optimized */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg overflow-hidden relative">
                <StoreImage
                  imageKey={store.logoUrl || null}
                  type="logo"
                  className="w-full h-full object-cover"
                  alt={store.name}
                  storeId={store.id}
                />
                {!store.logoUrl && (
                  <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-white absolute" />
                )}
              </div>
            </div>

            {/* Store Info - iPhone Optimized */}
            <div className="flex-1 text-center md:text-left">
              <div className="mb-3 sm:mb-4">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1.5 sm:mb-2 px-2 sm:px-0">{store.name}</h1>
                {store.description && (
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3 px-2 sm:px-0">{store.description}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4">
                {store.rating != null && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-current" />
                    <span className="font-semibold text-sm sm:text-base text-gray-900">
                      {typeof store.rating === 'number' ? store.rating.toFixed(1) : '0.0'}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-600">
                      ({store.reviewCount || 0} reseñas)
                    </span>
                  </div>
                )}
                <div className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                  isOpen
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {isOpen ? 'Abierto' : 'Cerrado'}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm text-gray-600">
                {store.phone && (
                  <div className="flex items-center justify-center md:justify-start gap-1.5 sm:gap-2">
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="break-all">{store.phone}</span>
                  </div>
                )}
                {store.email && (
                  <div className="flex items-center justify-center md:justify-start gap-1.5 sm:gap-2">
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="break-all">{store.email}</span>
                  </div>
                )}
                <div className="flex items-center justify-center md:justify-start gap-1.5 sm:gap-2">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="break-words">{store.address}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services and Info - iPhone Optimized */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-6">
          {/* Services - iPhone Optimized */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 md:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Servicios</h2>
            <div className="space-y-2.5 sm:space-y-3">
              {store.offersDelivery && (
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900">Entrega</p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      RD${Number(store.deliveryFee || 0).toFixed(2)} • {Number(store.deliveryTime) || 30} min
                    </p>
                  </div>
                </div>
              )}
              {store.offersKitchenService && (
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900">Servicio de Cocina</p>
                    <p className="text-xs sm:text-sm text-gray-600">Preparación de alimentos</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order Info - iPhone Optimized */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 md:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Información de Pedidos</h2>
            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-gray-600">Pedido Mínimo</span>
                <span className="font-semibold text-sm sm:text-base text-gray-900">
                  RD${Number(store.minOrderAmount || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-gray-600">Costo de Entrega</span>
                <span className="font-semibold text-sm sm:text-base text-gray-900">
                  RD${Number(store.deliveryFee || 0).toFixed(2)}
                </span>
              </div>
              {store.deliveryTime && (
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Tiempo Estimado</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {store.deliveryTime} min
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Featured Products - iPhone Optimized */}
        {featuredProducts.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Productos Destacados</h2>
              <button
                onClick={handleViewCatalog}
                className="text-primary-600 hover:text-primary-700 font-medium text-xs sm:text-sm min-h-[44px] px-2"
              >
                Ver Todo
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3 md:gap-4">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer active:scale-95"
                  onClick={() => navigate(`/stores/${store.id}/catalog`)}
                >
                  <div className="aspect-square bg-gray-100">
                    <ProductImage
                      imageKey={product.imageUrl || null}
                      className="w-full h-full object-cover"
                      alt={product.name}
                    />
                  </div>
                  <div className="p-2 sm:p-3">
                    <h3 className="font-medium text-gray-900 text-xs sm:text-sm mb-1 line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-primary-600 font-bold text-xs sm:text-sm">
                      RD${(Number(product.price) || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Button - iPhone Optimized */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 md:p-6 text-center mb-4 sm:mb-6 safe-area-bottom">
          <button
            onClick={handleViewCatalog}
            disabled={!isOpen}
            className={`w-full md:w-auto px-6 sm:px-8 py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-colors min-h-[44px] ${
              isOpen
                ? 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isOpen ? 'Ver Catálogo Completo' : 'Negocio Cerrado'}
          </button>
        </div>
      </div>
    </div>
    </>
  )
}

export default StoreProfile
